import type { StationThresholds } from './Thresholds';
import type { AgroForecastHour } from './Weather';

// Estación: el módulo LoRa con lo que tenga enchufado — clima (temperatura,
// humedad relativa y presión), pluviómetro, viento (velocidad + dirección) y sondas de
// suelo. Las métricas de un componente que no tiene quedan sin datos (null).
// Acá viven los tipos que devuelve el backend y las reglas "inteligentes"
// derivadas (punto de rocío, Delta T para pulverizar, riesgo de helada...),
// sin nada de React ni de HTTP.

export type StationMetric = 'temperature' | 'humidity' | 'pressure' | 'rain' | 'windSpeed' | 'windDirection';
export type StationRange = '6h' | '12h' | '24h' | '7d' | '30d';

export interface StationLatestReading {
  value: number;
  recordedAt: string;
}

export interface StationUnitStats {
  min: number;
  max: number;
  avg: number;
  sum: number;
  count: number;
  minAt: string;
  maxAt: string;
}

export interface StationBucket {
  t: string;
  avg: number;
  min: number;
  max: number;
  sum: number;
  circularMean: number | null;
}

export interface WindRoseSector {
  sector: number;
  count: number;
  avgSpeed: number | null;
}

export interface WeatherStationOverview {
  range: StationRange;
  from: string;
  // Fin del rango (ahora): el gráfico llega hasta acá aunque no haya datos.
  to: string;
  // Cortes de conexión que tocan el rango (endedAt null = sigue sin conexión).
  outages: { startedAt: string; endedAt: string | null }[];
  bucketSeconds: number;
  current: Record<StationMetric, StationLatestReading | null>;
  stats: Record<StationMetric, StationUnitStats | null>;
  series: Record<StationMetric, StationBucket[]>;
  rain: {
    lastHour: number;
    today: number;
    last24h: number;
    last7d: number;
    last30d: number;
    daily: { date: string; mm: number }[];
  };
  windRose: WindRoseSector[];
  // Una entrada por sonda de suelo habilitada.
  soil: SoilProbeOverview[];
}

export interface SoilProbeOverview {
  channel: number;
  name: string;
  current: StationLatestReading | null;
  stats: StationUnitStats | null;
  series: StationBucket[];
}

// --- Indicadores agro (grados día, hoja mojada, heladas) ---
// Los calcula el backend sobre los datos por hora (ver backend/src/domain/entities/Agro.ts).

export interface AgroDay {
  date: string;
  tMin: number | null;
  tMax: number | null;
  // null = día sin datos suficientes, o hoy (que no terminó).
  gdd: number | null;
  wetHours: number | null;
  hours: number;
  // null = la estación no tiene pluviómetro.
  rainMm: number | null;
  // Evapotranspiración de referencia (Hargreaves); null sin día completo o sin ubicación.
  et0: number | null;
}

export interface FrostEvent {
  // Fecha de la tarde en que empieza la noche de la helada.
  night: string;
  minC: number;
  minAt: string;
  hours: number;
}

export interface StationAgro {
  since: string;
  baseTemp: number;
  days: AgroDay[];
  degreeDays: { total: number; countedDays: number; missingDays: number; last7Avg: number | null };
  frosts: FrostEvent[];
  rain: { total: number; monthly: { month: string; mm: number }[] } | null;
  // Balance = lluvia − evapotranspiración de los `days` días con ET0.
  water: { et0: number; balance: number | null; days: number; needsLocation: boolean };
}

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// "2026-07" -> "Jul"
export function monthLabel(month: string): string {
  return MONTHS[Number(month.slice(5, 7)) - 1];
}

// "2026-07-13" -> "13/07"
export function shortDate(date: string): string {
  const [, m, d] = date.split('-');
  return `${d}/${m}`;
}

// --- Las pantallas de detalle (viento agrupa velocidad + dirección) ---

export type StationDetailSlug = 'temperatura' | 'humedad' | 'presion' | 'lluvia' | 'viento';
export const STATION_DETAIL_SLUGS: StationDetailSlug[] = ['temperatura', 'humedad', 'presion', 'lluvia', 'viento'];

export const STATION_DETAIL_META: Record<StationDetailSlug, { title: string; metric: StationMetric; unit: string; color: string }> = {
  temperatura: { title: 'Temperatura', metric: 'temperature', unit: '°C', color: 'var(--domiot-metric-temp)' },
  humedad: { title: 'Humedad relativa', metric: 'humidity', unit: '%', color: 'var(--domiot-metric-humidity)' },
  presion: { title: 'Presión atmosférica', metric: 'pressure', unit: 'hPa', color: 'var(--domiot-metric-pressure)' },
  lluvia: { title: 'Lluvia', metric: 'rain', unit: 'mm', color: 'var(--domiot-metric-rain)' },
  viento: { title: 'Viento', metric: 'windSpeed', unit: 'km/h', color: 'var(--domiot-metric-wind)' },
};

// --- Presión ---

// Tendencia barométrica de las últimas 3 horas (la que usan las estaciones
// meteorológicas): lo que anticipa la presión es cómo cambia, no su valor.
// Bajar más de 1.6 hPa en 3 h suele anunciar un frente; más de 3.6, rápido.
export interface PressureTrend {
  delta: number;
  label: string;
  hint: string;
  level: InsightLevel;
}

export function pressureTrend(series: StationBucket[], current: StationLatestReading | null): PressureTrend | null {
  if (!current) return null;
  const now = new Date(current.recordedAt).getTime();
  // El tramo más cercano a 3 h antes (entre 2.5 y 4 h): con tramos diarios no hay.
  const target = now - 3 * 3600 * 1000;
  const past = series
    .filter((b) => {
      const t = new Date(b.t).getTime();
      return t <= now - 2.5 * 3600 * 1000 && t >= now - 4 * 3600 * 1000;
    })
    .sort((a, b) => Math.abs(new Date(a.t).getTime() - target) - Math.abs(new Date(b.t).getTime() - target))[0];
  if (!past) return null;
  const delta = Math.round((current.value - past.avg) * 10) / 10;
  if (delta <= -3.6) return { delta, label: 'Bajando rápido', hint: 'Se acerca un frente: probable mal tiempo', level: 'bad' };
  if (delta <= -1.6) return { delta, label: 'Bajando', hint: 'Puede desmejorar', level: 'caution' };
  if (delta >= 3.6) return { delta, label: 'Subiendo rápido', hint: 'Mejora, con posible viento', level: 'ok' };
  if (delta >= 1.6) return { delta, label: 'Subiendo', hint: 'Tiende a mejorar', level: 'ok' };
  return { delta, label: 'Estable', hint: 'Sin cambios de tiempo a la vista', level: 'ok' };
}

// Humedad de suelo según los umbrales de la estación (dependen del suelo y del cultivo).
export function soilMoistureLabel(pct: number, t: StationThresholds['soil']): { level: InsightLevel; label: string } {
  if (pct < t.dryPct) return { level: 'bad', label: 'Seco' };
  if (pct < t.lowPct) return { level: 'caution', label: 'Bajando' };
  if (pct > t.saturatedPct) return { level: 'caution', label: 'Saturado' };
  return { level: 'ok', label: 'Bien' };
}

export const STATION_RANGE_LABELS: Record<StationRange, string> = {
  '6h': '6 h',
  '12h': '12 h',
  '24h': '24 h',
  '7d': '7 días',
  '30d': '30 días',
};

// Rangos de horas (no de días): el eje muestra la hora y la lluvia se ve por
// intervalo, no por día calendario.
export function isHourlyRange(range: StationRange): boolean {
  return range !== '7d' && range !== '30d';
}

// --- Viento ---

const COMPASS_16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];
const COMPASS_8_NAMES = ['Norte', 'Noreste', 'Este', 'Sudeste', 'Sur', 'Sudoeste', 'Oeste', 'Noroeste'];

export function compassPoint(degrees: number): string {
  return COMPASS_16[Math.round((((degrees % 360) + 360) % 360) / 22.5) % 16];
}

export function compassSectorLabel(sector: number): string {
  return COMPASS_16[sector % 16];
}

export function compassName(degrees: number): string {
  return COMPASS_8_NAMES[Math.round((((degrees % 360) + 360) % 360) / 45) % 8];
}

// Escala de Beaufort (en km/h), con los nombres del Servicio Meteorológico.
const BEAUFORT_LIMITS = [1, 6, 12, 20, 29, 39, 50, 62, 75, 89, 103, 118];
const BEAUFORT_NAMES = [
  'Calma',
  'Ventolina',
  'Brisa muy débil',
  'Brisa débil',
  'Brisa moderada',
  'Brisa fresca',
  'Brisa fuerte',
  'Viento fuerte',
  'Temporal',
  'Temporal fuerte',
  'Temporal duro',
  'Temporal muy duro',
  'Huracán',
];

export function beaufort(kmh: number): { force: number; name: string } {
  const force = BEAUFORT_LIMITS.findIndex((limit) => kmh < limit);
  const f = force === -1 ? 12 : force;
  return { force: f, name: BEAUFORT_NAMES[f] };
}

// --- Humedad y temperatura ---

// Fórmula de Magnus: temperatura a la que el aire se satura y aparece rocío
// (o escarcha, si es bajo cero).
export function dewPoint(tempC: number, rh: number): number {
  const a = 17.62;
  const b = 243.12;
  const gamma = Math.log(Math.max(rh, 1) / 100) + (a * tempC) / (b + tempC);
  return (b * gamma) / (a - gamma);
}

// Temperatura de bulbo húmedo (Stull, 2011) — base del Delta T.
export function wetBulb(tempC: number, rh: number): number {
  const T = tempC;
  const RH = Math.min(Math.max(rh, 5), 99);
  return (
    T * Math.atan(0.151977 * Math.sqrt(RH + 8.313659)) +
    Math.atan(T + RH) -
    Math.atan(RH - 1.676331) +
    0.00391838 * RH ** 1.5 * Math.atan(0.023101 * RH) -
    4.686035
  );
}

export function deltaT(tempC: number, rh: number): number {
  return tempC - wetBulb(tempC, rh);
}

// Sensación térmica (temperatura aparente de Steadman, la que usa el BoM
// australiano): combina temperatura, humedad y viento en una sola fórmula
// que sirve tanto con frío como con calor.
export function apparentTemperature(tempC: number, rh: number, windKmh: number): number {
  const e = (rh / 100) * 6.105 * Math.exp((17.27 * tempC) / (237.7 + tempC));
  const ws = windKmh / 3.6;
  return tempC + 0.33 * e - 0.7 * ws - 4;
}

export type InsightLevel = 'ok' | 'caution' | 'bad';

export interface Insight {
  level: InsightLevel;
  title: string;
  detail: string;
}

// La temperatura del abrigo meteorológico (1.5 m) es más alta que la del
// suelo en una noche despejada: con unos grados arriba de cero ya puede
// helar a ras del cultivo (helada agronómica). Los umbrales son de la estación.
export function frostInsight(tempC: number | null, minTempC: number | null, t: StationThresholds['frost']): Insight | null {
  const reference = minTempC ?? tempC;
  if (reference === null) return null;
  if (reference <= t.frostC) return { level: 'bad', title: 'Helada', detail: `La temperatura bajó a ${round1(reference)}°C.` };
  if (reference <= t.riskC)
    return { level: 'caution', title: 'Riesgo de helada', detail: `Mínima de ${round1(reference)}°C: puede helar a nivel del suelo.` };
  return null;
}

export function humidityInsight(rh: number | null, t: StationThresholds['airHumidity']): Insight | null {
  if (rh === null) return null;
  if (rh >= t.highPct) return { level: 'caution', title: 'Humedad muy alta', detail: 'Favorece enfermedades por hongos y el mojado de hoja.' };
  if (rh <= t.lowPct) return { level: 'caution', title: 'Aire muy seco', detail: 'Más evaporación y estrés hídrico en el cultivo.' };
  return null;
}

// Condiciones para pulverizar: Delta T dentro de la ventana ideal (las gotas
// no se evaporan antes de llegar ni quedan flotando) y viento moderado (con
// calma total suele haber inversión térmica y deriva). Los umbrales son de
// la estación (por defecto Delta T 2–8 y viento 3–15 km/h).
// Primera hora del pronóstico (desde la actual) dentro de las próximas
// `hours` con lluvia probable: al menos 0,2 mm o 50% de probabilidad.
export interface RainSoon {
  inHours: number;
  mm: number;
  probability: number;
}

export function rainSoon(forecastHours: AgroForecastHour[], hours: number): RainSoon | null {
  const index = forecastHours.slice(0, hours).findIndex((h) => h.precipitation >= 0.2 || h.precipitationProbability >= 50);
  if (index === -1) return null;
  const h = forecastHours[index];
  return { inHours: index, mm: h.precipitation, probability: h.precipitationProbability };
}

export function sprayInsight(
  tempC: number | null,
  rh: number | null,
  windKmh: number | null,
  rainingNow: boolean,
  t: StationThresholds['spray'],
  // null = sin pronóstico (no se sabe) o sin lluvia a la vista.
  rainAhead: RainSoon | null = null
): Insight | null {
  if (tempC === null || rh === null || windKmh === null) return null;
  const dt = deltaT(tempC, rh);
  const reasons: string[] = [];
  let level: InsightLevel = 'ok';
  const worsen = (to: InsightLevel) => {
    if (to === 'bad' || (to === 'caution' && level === 'ok')) level = to;
  };

  if (rainingNow) {
    worsen('bad');
    reasons.push('está lloviendo');
  } else if (rainAhead) {
    // Lluvia firme (≥ 1 mm y ≥ 60%) lava el producto; si es solo probable, precaución.
    const when = rainAhead.inHours === 0 ? 'en la próxima hora' : `en ${rainAhead.inHours + 1} h`;
    if (rainAhead.mm >= 1 && rainAhead.probability >= 60) {
      worsen('bad');
      reasons.push(`lluvia prevista ${when} (${round1(rainAhead.mm)} mm), puede lavar el producto`);
    } else {
      worsen('caution');
      reasons.push(`posible lluvia ${when} (${Math.round(rainAhead.probability)}%)`);
    }
  }
  if (dt < t.deltaTMin) {
    worsen('caution');
    reasons.push(`Delta T bajo (${round1(dt)})`);
  } else if (dt > t.deltaTLimit) {
    worsen('bad');
    reasons.push(`Delta T alto (${round1(dt)}), mucha evaporación`);
  } else if (dt > t.deltaTMax) {
    worsen('caution');
    reasons.push(`Delta T en el límite (${round1(dt)})`);
  }
  if (windKmh < t.windMin) {
    worsen('caution');
    reasons.push('viento calmo, posible inversión térmica');
  } else if (windKmh > t.windLimit) {
    worsen('bad');
    reasons.push(`viento fuerte (${Math.round(windKmh)} km/h)`);
  } else if (windKmh > t.windMax) {
    worsen('caution');
    reasons.push(`viento en el límite (${Math.round(windKmh)} km/h)`);
  }

  const titles: Record<InsightLevel, string> = {
    ok: 'Buenas condiciones para pulverizar',
    caution: 'Pulverizar con precaución',
    bad: 'No conviene pulverizar',
  };
  const detail =
    reasons.length === 0
      ? `Delta T ${round1(dt)} y viento de ${Math.round(windKmh)} km/h, dentro de la ventana ideal.`
      : capitalize(reasons.join(', ')) + '.';
  return { level, title: titles[level], detail };
}

export interface StationInsight extends Insight {
  kind: 'spray' | 'frost' | 'humidity';
}

// Las lecturas "inteligentes" que se muestran juntas en el home y en los
// detalles, en orden de importancia para el día a día del campo.
export function stationInsights(
  tempC: number | null,
  rh: number | null,
  windKmh: number | null,
  rainLastHour: number,
  minTempC: number | null,
  t: StationThresholds,
  forecastHours: AgroForecastHour[] = []
): StationInsight[] {
  const items: StationInsight[] = [];
  const spray = sprayInsight(tempC, rh, windKmh, rainLastHour > 0, t.spray, rainSoon(forecastHours, t.spray.rainFreeHours));
  if (spray) items.push({ ...spray, kind: 'spray' });
  const frost = frostInsight(tempC, minTempC, t.frost);
  if (frost) items.push({ ...frost, kind: 'frost' });
  const humidity = humidityInsight(rh, t.airHumidity);
  if (humidity) items.push({ ...humidity, kind: 'humidity' });
  return items;
}

// --- Lluvia ---

export function rainIntensityLabel(mmPerHour: number): string {
  if (mmPerHour <= 0) return 'Sin lluvia';
  if (mmPerHour < 2.5) return 'Lluvia débil';
  if (mmPerHour < 7.6) return 'Lluvia moderada';
  if (mmPerHour < 50) return 'Lluvia fuerte';
  return 'Lluvia torrencial';
}

export function rainyDays(daily: { mm: number }[], minMm = 0.2): number {
  return daily.filter((d) => d.mm >= minMm).length;
}

// Días seguidos sin lluvia hasta hoy (útil para saber cuánto hace que no llueve).
export function daysSinceRain(daily: { date: string; mm: number }[], minMm = 0.2): number | null {
  for (let i = daily.length - 1; i >= 0; i--) {
    if (daily[i].mm >= minMm) return daily.length - 1 - i;
  }
  return null;
}

// --- Varios ---

export const round1 = (n: number) => Math.round(n * 10) / 10;
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function formatRelative(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'recién';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} día${days > 1 ? 's' : ''}`;
}
